import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import {
  AutoCompleteUncontrolled,
  ISelectType,
  OverflowTooltip,
  Table,
  useFetch,
} from "@4uhub/lib4uhub";
import MainApi from "../../../services/mainApi.service";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IGetDonor } from "../../../models/donor";
import { Stack } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { set } from "lodash";
import UsersService from "../../../services/users.service";

const DONOR_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Donor/";
const donorService = new MainApi<IGetDonor>(DONOR_ROUTE);
const usersService = new UsersService();

const translationPath = "page.4uDonation.donors.";

const joinWhatsappContacts = (contactsList: IGetDonor["contacts"]) => {
  const whatsappValues = contactsList!
    .filter((contact) => contact.donorContactType.code === "1")
    .map((contact) => contact.value);
  const joinContacts = whatsappValues.join(", ");

  return joinContacts ? joinContacts : "-";
};

export const DonorsPage = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [attendant, setSelectedAttendant] = useState<ISelectType | null>(null);
  const [users, setUsers] = useState<ISelectType[]>([]);

  const { sendRequest } = useFetch(usersService.listUsers);

  const loadFilteredUsers = useCallback(async () => {
    const { data, success } = await sendRequest(undefined);
    if (data && success) {
      setUsers(data.map((user) => ({ id: user.id, name: user.fullName })));
    }
  }, [sendRequest]);

  useEffect(() => {
    loadFilteredUsers();
  }, [loadFilteredUsers]);

  const columns: GridColDef<IGetDonor>[] = [
    {
      field: "name",
      headerName: t(translationPath + "name") || "Name",
    },
    {
      field: "document",
      headerName: t(translationPath + "document") || "Document",
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "code",
      headerName: t(translationPath + "donor_code") || "Donor Code",
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "contacts",
      headerName:
        t(translationPath + "whatsapp_contacts") || "Whatsapp Contacts",
      renderCell: ({ row: { contacts } }) => {
        if (!contacts || contacts.length === 0) return "-";
        const whatsappContacts = joinWhatsappContacts(contacts);
        return (
          <OverflowTooltip
            text={whatsappContacts}
            tooltip={whatsappContacts}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
    {
      field: "integrationId",
      headerName:
        t(translationPath + "donor_integration_id") || "Integration Code",
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "attendantIntegrationId",
      headerName:
        t(translationPath + "attendant_integration_id") || "Integration Code",
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            textTransform="none"
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "attendant",
      headerName: t(translationPath + "attendant") || "attendant",
      renderCell: ({ row: { attendant } }) => {
        if (!attendant) return "-";
        return (
          <OverflowTooltip
            text={attendant.fullName}
            tooltip={attendant.fullName}
            textTransform="none"
            variant="body2"
          />
        );
      },
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<IGetDonor>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const props = useMemo(() => {
    return {
      AttendantId: attendant ? attendant.id : undefined,
    };
  }, [attendant]);

  return (
    <Table<IGetDonor>
      showDefaultMacroFilters={false}
      service={donorService}
      columns={columns}
      title={t(translationPath + "title")}
      searchInput
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Searh for Donor"
      }
      serviceProps={props}
      macroFilters={() => (
        <>
          <Stack width={300}>
            <AutoCompleteUncontrolled
              label={t(translationPath + "attendant") || "Attendant"}
              onValueSelected={setSelectedAttendant}
              value={attendant}
              size="small"
              fullWidth
              options={users}
            />
          </Stack>
        </>
      )}
      addButtonLabel={t(translationPath + "add") || "Add Donor"}
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};
